import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Login.css';

// Configure axios with the API base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const axiosInstance = axios.create({
  baseURL: API_URL,
});

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('Invalid reset link');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get<{ email: string; valid: boolean }>(`/auth/reset-password/${token}`);
      setEmail(response.data.email);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid or expired reset link');
      setLoading(false);
    }
  };

  const validatePassword = (value: string): string => {
    if (value.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/\d/.test(value)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const error = validatePassword(value);
    setPasswordError(error);
    
    if (confirmPassword) {
      setConfirmPasswordError(value !== confirmPassword ? 'Passwords do not match' : '');
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    setConfirmPasswordError(value !== password ? 'Passwords do not match' : '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const pwdError = validatePassword(password);
    if (pwdError) {
      setPasswordError(pwdError);
      return;
    }
    
    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axiosInstance.post('/auth/reset-password', {
        token,
        password
      });

      navigate('/login', { 
        state: { 
          message: 'Password successfully reset! Please log in with your new password.' 
        } 
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="login-container">
        <div className="login-header">
          <h1>ComplexLaw</h1>
          <h2>PCP Claims Management System</h2>
        </div>
        <div className="login-form-container">
          <div className="login-card">
            <p>Validating reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !email) {
    return (
      <div className="login-container">
        <div className="login-header">
          <h1>ComplexLaw</h1>
          <h2>PCP Claims Management System</h2>
        </div>
        <div className="login-form-container">
          <div className="login-card">
            <h2>Invalid Reset Link</h2>
            <div className="error-message">{error}</div>
            <button 
              className="login-button"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-header">
        <h1>ComplexLaw</h1>
        <h2>PCP Claims Management System</h2>
      </div>
      
      <div className="login-form-container">
        <div className="login-card">
          <h2>Reset Your Password</h2>
          
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '1.5rem' }}>
            Enter a new password for <strong>{email}</strong>
          </p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePasswordChange(e.target.value)}
                  required
                  placeholder="At least 8 characters with 1 number"
                  style={{ paddingRight: '3rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    fontSize: '1.2rem',
                    color: '#666'
                  }}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {passwordError && <span style={{ color: '#dc3545', fontSize: '0.875rem' }}>{passwordError}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleConfirmPasswordChange(e.target.value)}
                  required
                  placeholder="Re-enter your password"
                  style={{ paddingRight: '3rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    fontSize: '1.2rem',
                    color: '#666'
                  }}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {confirmPasswordError && <span style={{ color: '#dc3545', fontSize: '0.875rem' }}>{confirmPasswordError}</span>}
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={submitting || !!passwordError || !!confirmPasswordError}
            >
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;